import { computed, ref, watch, type Ref } from 'vue';
import type { PlaygroundCategory, PlaygroundScores } from '@/types/api';
import { savePlaygroundScores } from '@/lib/apiClient';

type PendingPayload = {
  userId: number;
  pending: Record<PlaygroundCategory, number>;
  bonusRolls: Record<PlaygroundCategory, number[]>;
};

const STORAGE_PREFIX = 'hp_clickers_pending_';

function emptyPending(): Record<PlaygroundCategory, number> {
  return { money: 0, health: 0, love: 0 };
}

function emptyBonusRolls(): Record<PlaygroundCategory, number[]> {
  return { money: [], health: [], love: [] };
}

function sumRolls(rolls: number[]) {
  return rolls.reduce((sum, amount) => sum + amount, 0);
}

function loadPending(userId: number): PendingPayload {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${userId}`);
    if (!raw) {
      return { userId, pending: emptyPending(), bonusRolls: emptyBonusRolls() };
    }
    const parsed = JSON.parse(raw) as Partial<PendingPayload> & {
      bonusPending?: Record<PlaygroundCategory, number>;
    };
    if (parsed.userId !== userId) {
      return { userId, pending: emptyPending(), bonusRolls: emptyBonusRolls() };
    }

    const bonusRolls = parsed.bonusRolls
      ? {
          money: [...(parsed.bonusRolls.money ?? [])],
          health: [...(parsed.bonusRolls.health ?? [])],
          love: [...(parsed.bonusRolls.love ?? [])],
        }
      : emptyBonusRolls();

    return {
      userId,
      pending: { ...emptyPending(), ...parsed.pending },
      bonusRolls,
    };
  } catch {
    return { userId, pending: emptyPending(), bonusRolls: emptyBonusRolls() };
  }
}

function savePending(payload: PendingPayload) {
  localStorage.setItem(`${STORAGE_PREFIX}${payload.userId}`, JSON.stringify(payload));
}

export function usePlaygroundPending(userId: Ref<number | undefined>) {
  const serverScores = ref<PlaygroundScores>({ money: 0, health: 0, love: 0, total: 0 });
  const pending = ref<Record<PlaygroundCategory, number>>(emptyPending());
  const bonusRolls = ref<Record<PlaygroundCategory, number[]>>(emptyBonusRolls());

  function persist() {
    if (!userId.value) return;
    savePending({
      userId: userId.value,
      pending: { ...pending.value },
      bonusRolls: {
        money: [...bonusRolls.value.money],
        health: [...bonusRolls.value.health],
        love: [...bonusRolls.value.love],
      },
    });
  }

  watch(
    userId,
    (id) => {
      if (!id) return;
      const stored = loadPending(id);
      pending.value = stored.pending;
      bonusRolls.value = stored.bonusRolls;
    },
    { immediate: true },
  );

  const displayScores = computed<PlaygroundScores>(() => {
    const money = serverScores.value.money + pending.value.money + sumRolls(bonusRolls.value.money);
    const health =
      serverScores.value.health + pending.value.health + sumRolls(bonusRolls.value.health);
    const love = serverScores.value.love + pending.value.love + sumRolls(bonusRolls.value.love);
    return { money, health, love, total: money + health + love };
  });

  function setServerScores(scores: PlaygroundScores) {
    serverScores.value = scores;
  }

  function addClick(category: PlaygroundCategory) {
    pending.value = { ...pending.value, [category]: pending.value[category] + 1 };
    persist();
  }

  function addBonus(category: PlaygroundCategory, amount: number) {
    if (amount <= 0) return;
    bonusRolls.value = {
      ...bonusRolls.value,
      [category]: [...bonusRolls.value[category], amount],
    };
    persist();
  }

  function clearPending() {
    pending.value = emptyPending();
    bonusRolls.value = emptyBonusRolls();
    persist();
  }

  const hasPending = computed(
    () =>
      pending.value.money +
        pending.value.health +
        pending.value.love +
        sumRolls(bonusRolls.value.money) +
        sumRolls(bonusRolls.value.health) +
        sumRolls(bonusRolls.value.love) >
      0,
  );

  const savePayload = computed(() => ({
    money: pending.value.money,
    health: pending.value.health,
    love: pending.value.love,
    bonusMoney: bonusRolls.value.money.filter((amount) => amount > 0),
    bonusHealth: bonusRolls.value.health.filter((amount) => amount > 0),
    bonusLove: bonusRolls.value.love.filter((amount) => amount > 0),
  }));

  async function flushToServer(token: string): Promise<boolean> {
    if (!hasPending.value) return false;

    const response = await savePlaygroundScores(token, savePayload.value);
    setServerScores(response.scores);
    clearPending();
    return true;
  }

  return {
    serverScores,
    displayScores,
    pending,
    bonusRolls,
    hasPending,
    savePayload,
    setServerScores,
    addClick,
    addBonus,
    clearPending,
    flushToServer,
  };
}
